import {
  ChangeOrderChangedField,
  ChangeOrderStatus,
  ChangeOrderSummaryEntry,
  ChangeOrderUnchangedEntry,
  FinanceType,
} from 'types';

import { api } from './api';

export interface ChangeOrderReviewSigner {
  name: string;
  email: string;
  hasAccepted: boolean;
}

export interface ChangeOrderReviewResponse {
  status: ChangeOrderStatus;
  changedFields: ChangeOrderChangedField[];
  changeSummary: ChangeOrderSummaryEntry[];
  changeSummaryUnchanged: ChangeOrderUnchangedEntry[];
  epcName: string;
  originalContractUrl: string | null;
  originalContractDocumentId: string | null;
  contractHtml: string | null;
  financeType: FinanceType | null;
  sequence: number;
  signers: ChangeOrderReviewSigner[];
  expiresAt: string | null;
}

interface ReviewQueryParams {
  accountId: string;
  changeOrderId: string;
  inviteToken: string | null;
}

export const changeOrderOfferApi = api.injectEndpoints({
  endpoints: (build) => ({
    getChangeOrderReview: build.query<ChangeOrderReviewResponse, ReviewQueryParams>({
      query: ({ accountId, changeOrderId, inviteToken }) => ({
        url: `/v3/accounts/${accountId}/change-orders/${changeOrderId}/review`,
        params: inviteToken ? { inviteToken } : undefined,
      }),
      providesTags: () => [{ type: 'Quotes', id: 'CHANGE_ORDER_REVIEW' }],
    }),
    acceptChangeOrder: build.mutation<void, ReviewQueryParams>({
      query: ({ accountId, changeOrderId, inviteToken }) => ({
        url: `/v3/accounts/${accountId}/change-orders/${changeOrderId}/accept`,
        method: 'POST',
        params: inviteToken ? { inviteToken } : undefined,
      }),
      async onQueryStarted({ accountId, changeOrderId, inviteToken }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            changeOrderOfferApi.util.updateQueryData(
              'getChangeOrderReview',
              { accountId, changeOrderId, inviteToken },
              (draft) => {
                const currentSigner = draft.signers[0];
                if (currentSigner) {
                  currentSigner.hasAccepted = true;
                }
              },
            ),
          );
        } catch {
          // mutation failed — no cache update needed
        }
      },
    }),
    declineChangeOrder: build.mutation<void, ReviewQueryParams & { reason: string }>({
      query: ({ accountId, changeOrderId, inviteToken, reason }) => ({
        url: `/v3/accounts/${accountId}/change-orders/${changeOrderId}/decline`,
        method: 'POST',
        body: { reason },
        params: inviteToken ? { inviteToken } : undefined,
      }),
      async onQueryStarted({ accountId, changeOrderId, inviteToken }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            changeOrderOfferApi.util.updateQueryData(
              'getChangeOrderReview',
              { accountId, changeOrderId, inviteToken },
              (draft) => {
                draft.status = ChangeOrderStatus.declined;
              },
            ),
          );
        } catch {
          // mutation failed — no cache update needed
        }
      },
    }),
  }),
});

export const { useGetChangeOrderReviewQuery, useAcceptChangeOrderMutation, useDeclineChangeOrderMutation } =
  changeOrderOfferApi;
